import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { SyllabusQuery, TopicLevel2StoreModel } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { FormConfirmFn } from '@examdojo/core/form-submit-button';
import {
  DEFAULT_SWIPER_STEPPER_OPTIONS,
  SwiperSlideDirective,
  SwiperStepperComponent,
  SwiperStepperNextDirective,
} from '@examdojo/core/swiper';
import { LoaderComponent } from '@examdojo/core/loader';
import { SwiperProgressComponent } from '@examdojo/ui/swiper';
import { SelectOption } from '@examdojo/util/select-option';
import { IonContent, ModalController } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { BehaviorSubject, map, of } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { StoryCharacter } from '../../../shared/sensei/character.model';
import { SenseiComponent } from '../../../shared/sensei/sensei.component';
import { AssessmentMode } from '../assessment-type.model';
import { AssessmentUIModel } from '../assessment.model';
import { AssessmentBasicComponent } from './assessment-basic/assessment-basic.component';
import { AssessmentFormModel } from './assessment-form-dialog.model';
import { ASSESSMENT_FORM_DIALOG_ID } from './assessment-form-dialog.service';
import { AssessmentTl2SelectorComponent } from './assessment-tl2-selector/assessment-tl2-selector.component';
import { AssessmentTypeSelectionComponent } from './assessment-type-selection/assessment-type-selection.component';

@Component({
  selector: 'dojo-assessment-form-dialog',
  imports: [
    AssessmentBasicComponent,
    IonContent,
    SwiperStepperComponent,
    FooterPanelComponent,
    SwiperStepperNextDirective,
    TranslocoPipe,
    SwiperSlideDirective,
    SwiperProgressComponent,
    AsyncPipe,
    ButtonComponent,
    SenseiComponent,
    LoaderComponent,
    AssessmentTypeSelectionComponent,
    AssessmentTl2SelectorComponent,
  ],
  templateUrl: './assessment-form-dialog.component.html',
  animations: [getAnimationAppearDisappear()],
  styleUrl: './assessment-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ion-page safe-area-top safe-area-bottom' },
})
export class AssessmentFormDialogComponent implements OnInit {
  constructor(
    private readonly syllabusQuery: SyllabusQuery,
    private readonly modalController: ModalController,
    private readonly renderer: Renderer2,
    private readonly destroyRef: DestroyRef,
  ) {
    // Subscribe to slide changes via the formStepper$ observable and update the speech bubble.
    this.formStepper$
      .pipe(
        filter(Boolean),
        switchMap((stepper: SwiperStepperComponent<FormGroup>) => stepper.activeSlideIndex$),
        tap((index: number) => {
          const slide = this.slides[index];
          this.updateActiveSpeechBubble(slide?.character?.speechBubble ?? '', slide?.character?.translationParams);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  @Input() assessment?: AssessmentUIModel;

  @ViewChild('container', { static: false }) container!: ElementRef;

  private readonly formStepper = viewChild(SwiperStepperComponent);
  private readonly formStepper$ = toObservable(this.formStepper);

  private readonly assessmentModes$ = this.syllabusQuery.active$.pipe(
    map((syllabus) => (syllabus?.assessment_modes ?? []).filter((mode) => mode !== 'FINAL_EXAM')),
  );

  readonly defaultSenseiCharacter: StoryCharacter = {
    file: 'assets/images/onboarding/hovering_sensei.riv',
    artboard: 'Floating Sensei',
    stateMachine: 'State Machine 1',
    isBackground: false,
    customStyles: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    mobileCustomStyles: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    widthPixel: 500,
    heightPixel: 500,
    isCentered: false,
  } as const;

  readonly activeSpeechBubble$$ = new BehaviorSubject<{
    text: string;
    translationParams?: Record<string, unknown>;
  } | null>(null);

  state = connectState({
    activeSpeechBubble: this.activeSpeechBubble$$,
  });

  readonly form = new FormGroup({
    // Step 1: Basic assessment info
    basicInfo: new FormGroup({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100)],
      }),
      date: new FormControl<Date | null>(null, {
        validators: [Validators.required],
      }),
      isActive: new FormControl(true, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }),
    // Step 2: Select assessment type

    typeSelection: new FormGroup({
      assessmentType: new FormControl<AssessmentMode[]>([], {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }),

    // Step 3: TL2-specific options (if applicable)
    tl2Selection: new FormGroup({
      selectedTopics: new FormControl<Array<TopicLevel2StoreModel['id']>>([], {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }),
  });

  slides = [
    {
      character: {
        ...this.defaultSenseiCharacter,
        speechBubble: 'onboarding.sensei_speech.basic_info',
      },
      formSettings: {
        formKey: 'basicInfo',
      },
    },
    {
      character: {
        ...this.defaultSenseiCharacter,
        speechBubble: 'onboarding.sensei_speech.topic_selection',
      },
      formSettings: {
        formKey: 'typeSelection',
        options$: this.assessmentModes$.pipe(
          map((assessmentModes) => {
            const syllabus = this.syllabusQuery.getActiveUIEntity();

            return assessmentModes.map((mode) => {
              const lowercaseMode = mode.toLowerCase();
              return {
                label: `assessment.assessment_type.${lowercaseMode}`,
                value: mode,
                labelDescription: `assessment.assessment_type.${lowercaseMode}_description`,
                logo: `assets/images/assessments/assessment_type/${lowercaseMode}_logo.svg`,
                figure: { type: 'icon', icon: `assets/images/assessments/${lowercaseMode}.svg` },
                translationParams: { program: syllabus?.program.short_name ?? '' },
              } satisfies SelectOption<AssessmentMode> & Record<string, unknown>;
            });
          }),
        ),
      },
    },
    {
      character: {
        ...this.defaultSenseiCharacter,
        speechBubble: 'onboarding.sensei_speech.tl2',
      },
      formSettings: {
        formKey: 'tl2Selection',
      },
    },
  ];

  readonly swiperStepperOptions = { ...DEFAULT_SWIPER_STEPPER_OPTIONS, autoHeight: true };

  readonly confirmFn: FormConfirmFn<typeof this.form> = () => {
    const formValue = this.form.getRawValue() as AssessmentFormModel;
    this.modalController.dismiss(formValue, undefined, ASSESSMENT_FORM_DIALOG_ID);
    this.form.reset();
    return of(true);
  };

  ngOnInit() {
    if (this.assessment) {
      this.bindForm(this.assessment);
    } else {
      this.assessmentModes$
        .pipe(
          tap((modes) => {
            if (!modes.length) {
              return;
            }
            this.form.controls.typeSelection.controls.assessmentType.setValue([modes[0]]);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  closeForm() {
    this.form.reset();
    this.modalController.dismiss(undefined, undefined, ASSESSMENT_FORM_DIALOG_ID);
  }

  onScroll(event: CustomEvent) {
    const targetElement = this.container?.nativeElement;
    const scrollTop = event.detail.scrollTop;

    if (!targetElement) {
      return;
    }
    if (scrollTop > 0) {
      this.renderer.addClass(targetElement, 'scrolled');
    } else {
      this.renderer.removeClass(targetElement, 'scrolled');
    }
  }

  private bindForm(assessment: AssessmentUIModel) {
    this.form.patchValue({
      basicInfo: {
        name: assessment.title,
        date: assessment.target_date,
        isActive: assessment.active!,
      },
      typeSelection: {
        assessmentType: assessment.assessment_type !== 'FINAL_EXAM' ? [assessment.assessment_type] : [],
      },
      tl2Selection: {
        selectedTopics: assessment.topicLevel2Ids,
      },
    });
  }

  private updateActiveSpeechBubble(text: string, translationParams?: Record<string, unknown>) {
    this.activeSpeechBubble$$.next({ text, translationParams });
  }
}
